import { useCallback } from "react";

import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import { useToast } from "@smart/bridge-intake-components-dom";
import { ContextMenu, ProgressCircle } from "@smart/itops-sb-design-system-dom";
import { useLazyLoadMatterPermissions } from "@smart/manage-gql-client-dom";

import { reminderActionLabel } from "../../shared/submission-actions/reminder-helpers";
import { CellProps, GqlSubmissionSummary } from "../../types";

type SubmissionActionProps = Record<
  "onCancel" | "onDelete" | "onRemind",
  (submission: GqlSubmissionSummary) => void
>;

export const PendingSubmissionAction =
  ({ onCancel, onDelete, onRemind }: SubmissionActionProps) =>
  ({ row }: CellProps<GqlSubmissionSummary>) => {
    const { setToasts } = useToast();
    const { getPermissions, loading } = useLazyLoadMatterPermissions();
    const { "LINK-3736": automaticRemindersFlag } = useFeatureFlags();
    const submission = row.original;
    const isInternalUse =
      submission.values.status === "draft" &&
      submission.values.communicationMethod === "internalUse";

    const onAction = useCallback(
      async (action: (input: GqlSubmissionSummary) => void) => {
        if (submission.values.matterId) {
          const permisions = await getPermissions({
            matterIds: [submission.values.matterId],
          });
          if (permisions?.length && permisions[0].canAccess === false) {
            setToasts([
              {
                palette: "warning",
                text: "You do not have permission to access to this matter. Please contact the Person Responsible or Firm Owner to request access.",
              },
            ]);
            return;
          }
        }

        action(submission);
      },
      [submission],
    );

    return (
      <>
        {loading && (
          <div className="fixed inset-0 z-modal flex justify-center items-center cursor-default">
            <ProgressCircle size="xSmall" />
          </div>
        )}
        <ContextMenu
          button={{
            className: "w-[2rem] h-[2rem]",
            variant: "secondarySubtle",
            leftIcon: "solidEllipsis",
          }}
          placement="bottom-end"
          offsetBy={5}
          items={[
            {
              leftIcon: isInternalUse ? "regularPlaneTop" : "regularBell",
              label: reminderActionLabel(
                isInternalUse,
                !!automaticRemindersFlag,
                "Send Reminder",
              ),
              onClick: () => onAction(onRemind),
            },
            {
              leftIcon: "regularXmark",
              label: "Close Form",
              onClick: () => onAction(onCancel),
            },
            {
              leftIcon: "regularTrashCan",
              label: "Delete Form",
              onClick: () => onAction(onDelete),
              className: "text-red-550",
            },
          ]}
          id="list-options"
          className="w-fit"
        />
      </>
    );
  };
